import MicrophoneButton from "@/components/customViews/MicrophoneButton";
import { Button } from "@/components/ui/button";
import Spinner from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import useMicrophone from "@/hooks/adapters/useMicrophone";
import { cn } from "@/lib/utils";
import type { AgentModelChoice } from "@cs_teamwaydev/mailvista-sdk_react";
import { ArrowUp, Calendar, Mail, Sparkles, Square } from "lucide-react";
import {
    FormEvent,
    ReactNode,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import AssistantLogo from "./AssistantLogo";
import ModelSelector from "./ModelSelector";

const SUGGESTIONS_PER_GROUP = 3;

const SUGGESTION_GROUPS = [
    {
        key: "discover",
        Icon: Sparkles,
        items: [
            "deletePromotions",
            "markNewsletterRead",
            "unprocessedUrgent",
            "projectPresentation",
            "meetingWithPresident",
            "powerBiTraining",
            "reportReminder",
            "securityTraining",
            "lunchFriday",
            "quarterlyPresentation",
            "interview",
        ],
    },
    {
        key: "mails",
        Icon: Mail,
        items: [
            "attachmentsThisWeek",
            "unreadFromContact",
            "budgetSinceJanuary",
            "fromAddressInMarch",
            "sentByClient",
            "invoicesFromAccounting",
        ],
    },
    {
        key: "agenda",
        Icon: Calendar,
        items: [
            "firstWeekOfMay",
            "today",
            "nextWeek",
            "availabilityTomorrowMorning",
            "april18",
            "nextFiveDays",
            "meetingsThisWeek",
            "nextThreeHours",
            "slotMondayAfternoon",
            "slotNextMonday",
            "twoHoursSoon",
            "clientCallSlots",
            "hourTomorrowMorning",
            "shortSlotsToday",
        ],
    },
];

const drawSuggestions = () =>
    SUGGESTION_GROUPS.map((group) => ({
        ...group,
        items: group.items
            .map((item) => ({ item, rank: Math.random() }))
            .sort((a, b) => a.rank - b.rank)
            .slice(0, SUGGESTIONS_PER_GROUP)
            .map(({ item }) => item),
    }));

const roundButtonClassName =
    "h-10 w-10 flex-none rounded-full bg-gradient-to-r from-assistant-send to-assistant-ink p-0 text-white hover:text-white hover:brightness-110 disabled:opacity-100 [&_svg]:size-5";

const MAX_INPUT_LENGTH = 1000;
// The input grows with the request, up to about six lines, then scrolls.
const MAX_INPUT_HEIGHT = 160;

const fitInputHeight = (input: HTMLTextAreaElement | null) => {
    if (!input) return;
    input.style.height = "auto";
    input.style.height = `${Math.min(input.scrollHeight, MAX_INPUT_HEIGHT)}px`;
};

interface AssistantComposerProps {
    inputValue: string;
    setInputValue: (inputValue: string) => void;
    handleSubmitInput: (e: FormEvent<HTMLFormElement>) => void;
    isSendingMessage: boolean;
    isWaitingForDeepAnalysis?: boolean;
    isLoadingMoreData?: boolean;
    placeholder: string;
    contextGauge?: ReactNode;
    /** The conversation's memory, next to the gauge (step-based contract). */
    memory?: ReactNode;
    modelChoice?: AgentModelChoice;
    onModelChoiceChange?: (choice: AgentModelChoice) => void;
    /** « Arrêter » while the assistant answers, in place of « Envoyer » (absent: a spinner). */
    onStop?: () => void;
    /** New conversation: a few request ideas under the bar. */
    showSuggestionGrid?: boolean;
}

const AssistantComposer = ({
    inputValue,
    setInputValue,
    handleSubmitInput,
    isSendingMessage,
    isWaitingForDeepAnalysis = false,
    isLoadingMoreData = false,
    placeholder,
    contextGauge,
    memory,
    modelChoice,
    onModelChoiceChange,
    onStop,
    showSuggestionGrid = false,
}: AssistantComposerProps) => {
    const { t } = useTranslation();
    const inputId = useId();
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [suggestionGroups] = useState(drawSuggestions);

    const {
        handleStartListening,
        handleStopListening,
        browserSupportsSpeechRecognition,
        isMicrophoneAvailable,
        isDictationDisabled,
        listening,
        resetTranscript,
    } = useMicrophone(setInputValue, MAX_INPUT_LENGTH);

    useLayoutEffect(() => fitInputHeight(inputRef.current), [inputValue]);

    // Measured too early (panel still closed, page still loading), the input stays tall until the
    // first key: measure it again once laid out, then whenever its width changes.
    useEffect(() => {
        const input = inputRef.current;
        if (!input || typeof ResizeObserver === "undefined") return undefined;
        let width: number | undefined;
        const observer = new ResizeObserver(() => {
            if (input.offsetWidth === width) return;
            width = input.offsetWidth;
            fitInputHeight(input);
        });
        observer.observe(input);

        return () => observer.disconnect();
    }, []);

    const isBusy =
        isSendingMessage || isWaitingForDeepAnalysis || isLoadingMoreData;

    const submit = (e: FormEvent<HTMLFormElement>) => {
        handleStopListening();
        resetTranscript();
        handleSubmitInput(e);
    };

    const pickSuggestion = (suggestion: string) => {
        setInputValue(suggestion);
        inputRef.current?.focus();
    };

    return (
        <div className="flex w-full flex-col">
            <form
                className={cn(
                    "flex w-full items-end gap-2 rounded-[28px] border border-assistant-outline bg-white py-[7px] pl-3 pr-[7px] transition-[border-color,box-shadow] duration-300 focus-within:border-primary-dark",
                    isSendingMessage && "assistant-thinking",
                )}
                onSubmit={submit}
            >
                {isSendingMessage && (
                    <span aria-hidden className="assistant-beam" />
                )}
                <AssistantLogo className="self-center" size="sm" />
                <label className="sr-only" htmlFor={inputId}>
                    {t("vistassistant.composer.label")}
                </label>
                <Textarea
                    ref={inputRef}
                    className="min-h-0 flex-1 resize-none overflow-y-auto px-0 py-2 text-base leading-6 text-primary-darkest placeholder:text-assistant-muted md:text-base"
                    disabled={isSendingMessage || isWaitingForDeepAnalysis}
                    id={inputId}
                    onChange={(e) => {
                        if (e.target.value.length > MAX_INPUT_LENGTH) return;
                        setInputValue(e.target.value);
                        resetTranscript();
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            submit(
                                new Event(
                                    "submit",
                                ) as unknown as FormEvent<HTMLFormElement>,
                            );
                        }
                    }}
                    placeholder={placeholder}
                    rows={1}
                    size={null}
                    value={inputValue}
                    variant="invisible"
                />
                <MicrophoneButton
                    browserSupportsSpeechRecognition={
                        browserSupportsSpeechRecognition
                    }
                    className="mb-0.5 h-9 w-9 flex-none rounded-full text-primary-darkest hover:bg-assistant-chip [&_svg]:size-5"
                    isDictationDisabled={isDictationDisabled}
                    isMicrophoneAvailable={isMicrophoneAvailable}
                    isMicrophoneTemporarilyDisabled={
                        isSendingMessage || isWaitingForDeepAnalysis
                    }
                    listening={listening}
                    startListening={handleStartListening}
                    stopListening={handleStopListening}
                />
                {isSendingMessage && onStop ? (
                    <Button
                        aria-label={t("vistassistant.composer.stop")}
                        className={cn(
                            roundButtonClassName,
                            "[&_svg]:size-3 [&_svg]:rounded-[2px]",
                        )}
                        onClick={onStop}
                        title={t("vistassistant.composer.stop")}
                        type="button"
                        variant="invisible"
                    >
                        <Square aria-hidden className="fill-current" />
                    </Button>
                ) : (
                    <Button
                        aria-label={t("customViewDialog.vistaInput.send")}
                        className={cn(
                            roundButtonClassName,
                            !isBusy &&
                                !inputValue.trim() &&
                                "bg-none bg-assistant-chipBorder",
                        )}
                        disabled={isBusy || !inputValue.trim()}
                        type="submit"
                        variant="invisible"
                    >
                        {isBusy ? (
                            <Spinner className="text-white" size="small" />
                        ) : (
                            <ArrowUp strokeWidth={1.75} />
                        )}
                    </Button>
                )}
            </form>
            <div className="flex min-h-7 items-center justify-between gap-2 pl-4 pr-1 pt-1">
                <p
                    className={cn(
                        "min-w-0 truncate text-assistant-muted",
                        showSuggestionGrid ? "text-xs" : "text-sm",
                    )}
                >
                    {t(
                        showSuggestionGrid
                            ? "vistassistant.composer.hint"
                            : "vistassistant.composer.disclaimer",
                    )}
                </p>
                <div className="flex flex-none items-center gap-0.5">
                    {contextGauge}
                    {memory}
                    {modelChoice && onModelChoiceChange && (
                        <ModelSelector
                            onChange={onModelChoiceChange}
                            value={modelChoice}
                        />
                    )}
                </div>
            </div>
            {showSuggestionGrid && (
                <div className="mt-3 grid w-full grid-cols-[repeat(auto-fit,minmax(200px,1fr))] items-start gap-4">
                    {suggestionGroups.map(({ key, Icon, items }) => (
                        <section
                            key={key}
                            aria-labelledby={`${inputId}-${key}`}
                            className="flex flex-col gap-2 rounded-4xl bg-white p-4"
                        >
                            <div className="flex items-center gap-2">
                                <span className="flex size-8 flex-none items-center justify-center rounded-lg bg-assistant-lavender text-primary-dark">
                                    <Icon
                                        aria-hidden
                                        className="size-[18px]"
                                        strokeWidth={1.5}
                                    />
                                </span>
                                <h2
                                    className="text-sm font-semibold text-primary-darkest"
                                    id={`${inputId}-${key}`}
                                >
                                    {t(
                                        `vistassistant.suggestions.${key}.title`,
                                    )}
                                </h2>
                            </div>
                            {items.map((item) => {
                                const suggestion = t(
                                    `vistassistant.suggestions.${key}.${item}`,
                                );

                                return (
                                    <button
                                        key={item}
                                        className="rounded-xl border border-transparent bg-assistant-chip px-3 py-2.5 text-left text-sm leading-5 text-primary-darkest transition-colors hover:border-assistant-chipBorder hover:bg-white"
                                        onClick={() =>
                                            pickSuggestion(suggestion)
                                        }
                                        type="button"
                                    >
                                        {suggestion}
                                    </button>
                                );
                            })}
                        </section>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AssistantComposer;
